import { fetchFamilyMembersCondensedOrThrow, fetchPangenomeAlignmentMembersOrThrow } from "../../../api/fetch.ts";
import type { components, SyntenyTypeEnum } from "../../../openapi";

export type GenomeMember = components["schemas"]["SyntenyFamilyMemberResponse"] & {
  contigId: number;
  contigName: string;
  organismId: number;
  organismName: string;
  familyId: number;
};

export async function getSequenceFamilyMembersByGenomeOrThrow(
  syntenyId: number,
  familyId: number,
): Promise<Map<number, GenomeMember[]>> {
  if (!familyId) {
    return new Map();
  }

  // Fetch family data once
  const familyMembers = await fetchPangenomeAlignmentMembersOrThrow(syntenyId, familyId);

  if (familyMembers == undefined) {
    return new Map();
  }

  // Create the maps for efficient lookup
  const membersByGenome = new Map<number, GenomeMember[]>();

  // Process all members and organize them into maps
  for (const genome of familyMembers) {
    const genomeMembers: GenomeMember[] = [];

    for (const contig of genome.contigs) {
      const contigMembers = contig.members.map((member) => ({
        ...member,
        contigId: contig.contigId,
        contigName: contig.contigName,
        organismId: genome.organismId,
        organismName: genome.organismName,
        familyId,
      }));

      // Add to genome members for this iteration
      genomeMembers.push(...contigMembers);
    }

    // Add to genome map
    if (genomeMembers.length > 0) {
      membersByGenome.set(genome.organismId, genomeMembers);
    }
  }

  return membersByGenome;
}

export async function getFeaturesFamilyMembersByGenomeOrThrow(
  familyId: number,
  syntenyOrgId: number,
  syntenyType: SyntenyTypeEnum,
  featureId: number,
): Promise<Map<number, GenomeMember[]>> {
  if (!familyId || !syntenyType) {
    return new Map();
  }

  // Fetch family data once
  const familyMembers = await fetchFamilyMembersCondensedOrThrow(syntenyOrgId, syntenyType, featureId);

  if (familyMembers == undefined) {
    return new Map();
  }

  // Create the maps for efficient lookup
  const membersByGenome = new Map<number, GenomeMember[]>();

  // Process all members and organize them into maps
  for (const genome of familyMembers) {
    const genomeMembers: GenomeMember[] = [];

    for (const contig of genome.contigs) {
      const contigMembers = contig.members.map((member) => ({
        ...member,
        contigId: contig.contigId,
        contigName: contig.contigName,
        organismId: genome.organismId,
        organismName: genome.organismName,
        familyId,
      }));

      // Add to genome members for this iteration
      genomeMembers.push(...contigMembers);
    }

    // Add to genome map
    if (genomeMembers.length > 0) {
      membersByGenome.set(genome.organismId, genomeMembers);
    }
  }

  return membersByGenome;
}
